import React, {useEffect, useMemo, useState} from 'react';
import {Modal} from "../../../../../components/modal/modal.jsx";
import CitizenHistoryActions from "../utils/citizen-history-actions.js";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import InputDatetime from "../../../../../components/input/input-datetime.jsx";
import InputTextarea from "../../../../../components/input/input-textarea.jsx";
import Button from "../../../../../components/button/button.jsx";
import projectUtils from "../../../../../utils/project-utils.js";
import {toast} from "react-toastify";
import moment from "moment";
import {PermissionEnum} from "../../../../../utils/enums/permission-enum.js";
import useHasPermission from "../../../../../hooks/use-has-permission.js";
import citizenHistoryService from "../../../../../services/web/citizen-history-service.js";
import InputFile from "../../../../../components/input/input-file.jsx";
import {useQuery} from "@tanstack/react-query";
import userService from "../../../../../services/web/user-service.js";
import {FaExternalLinkAlt} from "react-icons/fa";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import Dropzone from "../../../../../components/dropzone/dropzone.jsx";

const CitizenHistoryModal = ({id, open, setOpen, history, setHistory, refetch, citizen}) => {
    const hasPermission = useHasPermission();
    const navigate = navigateHook();

    const [currentAction, setCurrentAction] = useState(undefined);
    const [attachesDescription, setAttachesDescription] = useState({});

    useEffect(() => {
        setCurrentAction(history?.action)
        setAttachesDescription((history?.attaches ?? []).reduce((acc, attach) => {
            acc[attach.attachId] = attach.description ?? null;
            return acc;
        }, {}));
    }, [open]);

    const editHistory = hasPermission(PermissionEnum.SOS_CITIZEN_HISTORY_EDIT);
    const manageCitizen = hasPermission(PermissionEnum.SOS_MANAGE_CITIZEN);

    const canEdit = editHistory && manageCitizen;
    const disabled = !!history?.citizenId && (!canEdit || (currentAction && !CitizenHistoryActions.editables.includes(currentAction)));

    const {data: agents} = useQuery({
        queryFn: () => userService.optionsActiveUserName(),
        queryKey: ["active-users-options"],
    });

    const aggressorsOptions = useMemo(() => {
        if (!citizen) return [];

        const options = citizen?.aggressors.map((a, i) => ({value: a.itemId, label: a.name ?? "Agressor - " + i+1})) ?? [];
        options.unshift({value: "citizen", label: citizen.name})

        return options;
    }, [citizen]);

    const setAttaches = (value) => setHistory(prev => ({
        ...prev,
        attaches: typeof value === "function" ? value(prev?.attaches ?? []) : value
    }));

    const upsertHistory = () => {
        if (!history.dtAction || !history.action || !history.description) {
            projectUtils.swalErrorMessage("", "Preencha os campos obrigatórios")
            return;
        }

        let response;
        const form = {...history};
        form.userExecution = form.executionUserId

        delete form.aggressorId;

        if (form.id) {
            form.historyId = form.id
            delete form.attaches;

            response = citizenHistoryService.edit(JSON.stringify(form))
        } else {
            form.aggressorId = history.aggressorId !== "citizen" ? history.aggressorId : null;

            form.citizenId = id;
            form.dtAction = moment(form.dtAction).format("DD/MM/YYYY HH:mm");

            const formData = new FormData()
            const files = (history.attaches ?? []).filter(file => file?.new);

            form.attaches = files.map(file => ({
                attachId: file.attachId,
                name: file.name,
                size: file.size,
                extension: file.name.split('.').pop(),
                description: attachesDescription[file.attachId] ?? null
            }));

            files.forEach(file => formData.append("attaches[]", file));

            const formJson= JSON.stringify(form);
            const formBlob = new Blob([formJson], {
                type: 'application/json'
            });

            formData.append('history', formBlob);

            response = citizenHistoryService.create(formData)
        }

        response
            .then(() => {
                toast.success("Salvo com sucesso");
                setOpen(false);
                refetch();
            })
            .catch(err => {
                console.error(err);
                projectUtils.swalErrorMessage("", "Falha ao adicionar anotação");
            })
    }

    return (
        <Modal open={open} setOpen={setOpen} title={currentAction ? CitizenHistoryActions.toView(currentAction) : "Adicionar anotação"} overflow={true}>
            <Modal.Body className={"grid grid-cols-12 gap-3 content-start max-h-[80vh] overflow-y-auto"}>
                <SelectComponent
                    classNameWrapper={"col-span-3"}
                    value={history.aggressorId || "citizen"}
                    options={aggressorsOptions}
                    label={"Cidadão/Empresa"}
                    onChange={(value) => setHistory(prev => ({...prev, aggressorId: value?.value}))}
                    isDisabled={disabled || !!history?.citizenId}
                    isClearable={false}

                />

                <SelectComponent
                    classNameWrapper={"col-span-3"}
                    value={CitizenHistoryActions.toOption(history.action)}
                    options={CitizenHistoryActions.editableOptions()}
                    label={"Tipo *"}
                    onChange={(value) => setHistory(prev => ({...prev, action: value?.value}))}
                    isDisabled={disabled || !!history?.citizenId}
                />

                <SelectComponent
                    classNameWrapper={"col-span-3"}
                    value={history.executionUserId}
                    options={agents ?? []}
                    label={"Realizado por"}
                    onChange={(value) => setHistory(prev => ({...prev, executionUserId: value?.value}))}
                    isDisabled={disabled || !!history?.citizenId}
                />

                <InputDatetime
                    classNameWrapper={"col-span-3"}
                    id={"dtEnd"}
                    label={"Data *"}
                    selected={history.dtAction}
                    dateFormat="LL"
                    onChange={(date) => setHistory(prev => ({...prev, dtAction: date}))}
                    disabled={disabled || !!history?.citizenId}
                />

                <InputTextarea
                    value={history.description}
                    label="Descrição *"
                    className="col-span-12"
                    textAreaClass={"h-[200px]"}
                    onChange={({target}) => setHistory(prev => ({...prev, description: target.value}))}
                    disabled={disabled}
                />

                {
                    (history?.attaches?.length > 0 || !history?.citizenId) &&
                    <div className={"col-span-12"}>
                        <Dropzone
                            title={"Anexos"}
                            attaches={history?.attaches ?? []}
                            setAttaches={setAttaches}
                            attachesDescription={attachesDescription}
                            setAttachesDescription={setAttachesDescription}
                            disabled={disabled || !!history?.citizenId}
                        />
                    </div>
                }

                {
                    history?.scheduledVisitId &&
                    <span
                        className={"cursor-pointer flex gap-2 items-center col-span-2"}
                        onClick={() => navigate(`/sos/citizen-scheduled-visit/?schedule_id=${history?.scheduledVisitId}`, true)}
                    >
                        <FaExternalLinkAlt /> Abrir agendamento
                    </span>
                }

                {
                    history?.signature &&
                    <InputFile
                        className={"col-span-3"}
                        file={history.signature}
                        isRemove={false}
                        titleWithFile={"Visualizar assinatura"}
                    />
                }
            </Modal.Body>
            {
                !disabled &&
                <Modal.Footer>
                    <Button target={"primary"} onClick={upsertHistory} actionFeedbackType="save">
                        Salvar
                    </Button>
                </Modal.Footer>
            }
        </Modal>
    );
};

export default CitizenHistoryModal;